import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { readDev2222ModeFromSession, writeDev2222ModeToSession } from '../dev2222Session'
import { DevFeature2222Bar } from '../DevFeature2222Bar'
import {
  initialDevShellDocumentTheme,
  writeDevShellDocumentThemeToSession,
} from '../devShellSplashSession'
import { DevShellBar } from '../DevShellBar'
import {
  readFeature2222Claim,
  type Dev2222Mode,
  type Experience,
} from '../experience'
import {
  resolveShellEmbedForPathSegment,
  resolveShellEmbedWithoutToken,
  type ResolvedShellEmbed,
} from '../shellEmbed'
import { siteAppRoutePath } from '../siteAppRoutePath'
import { siteExpiredPath } from '../siteExpiredPath'
import { markLoadPhase } from '../loadPerformance'
import type { ShellDocumentTheme } from '../shellDocumentTheme'
import { applyShellDocumentTheme } from '../shellDocumentTheme'
import { SHELL_APP_TITLE } from '../shellTitle'
import { ShellSplash } from '../ShellSplash'
import { useDismissStaticShellSplash } from '../useDismissStaticShellSplash'
import { loadExperienceApp } from './loadExperienceApp'

type Phase =
  | { kind: 'loading' }
  | { kind: 'booting'; experience: Experience }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready'
      experience: Experience
      jwtPayload: Record<string, unknown> | null
      notice?: string
    }

function format2222ClaimForUi(payload: Record<string, unknown> | null): string {
  const v = readFeature2222Claim(payload)
  if (v === undefined) return '—'
  const t = v.trim()
  if (t.length <= 40) return t
  return `${t.slice(0, 40)}…`
}

type UnifiedShellProps = Readonly<{
  pathExperience?: Experience
}>

const shellDevUi = import.meta.env.DEV

function showShellDevChrome(token: string | undefined): boolean {
  return shellDevUi && !!token?.trim()
}

function prepareUnifiedLocationForEmbed(resolved: ResolvedShellEmbed): void {
  if (resolved.target.kind === 'expired-page') {
    window.history.replaceState(null, '', siteExpiredPath())
    return
  }
  if (resolved.target.kind === 'app-route') {
    window.history.replaceState(null, '', siteAppRoutePath(resolved.target.segment))
  }
}

export function UnifiedShell({ pathExperience }: UnifiedShellProps) {
  const { token } = useParams<{ token: string }>()
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  const authRequestRef = useRef<string | null>(null)
  const [dev2222Mode, setDev2222Mode] = useState<Dev2222Mode>(() => readDev2222ModeFromSession())
  const [documentTheme, setDocumentTheme] = useState<ShellDocumentTheme>(() =>
    initialDevShellDocumentTheme(),
  )

  useDismissStaticShellSplash()

  useEffect(() => {
    applyShellDocumentTheme(documentTheme)
  }, [documentTheme])

  const persistDev2222Mode = useCallback((mode: Dev2222Mode) => {
    writeDev2222ModeToSession(mode)
    setDev2222Mode(mode)
  }, [])

  const persistDocumentTheme = useCallback((theme: ShellDocumentTheme) => {
    writeDevShellDocumentThemeToSession(theme)
    setDocumentTheme(theme)
    applyShellDocumentTheme(theme)
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadExperience = async (resolved: ResolvedShellEmbed) => {
      setPhase({ kind: 'booting', experience: resolved.experience })
      try {
        prepareUnifiedLocationForEmbed(resolved)
        await loadExperienceApp(resolved.experience)
      } catch (e) {
        if (cancelled) return
        const message = e instanceof Error ? e.message : String(e)
        setPhase({ kind: 'error', message: `Failed to load ${resolved.experience}: ${message}` })
      }
    }

    if (!token?.trim()) {
      void loadExperience(resolveShellEmbedWithoutToken(pathExperience))
      return () => {
        cancelled = true
      }
    }

    const requestKey = `${token}:${pathExperience ?? ''}:${dev2222Mode}`
    if (authRequestRef.current === requestKey) return
    authRequestRef.current = requestKey

    void (async () => {
      const resolved = await resolveShellEmbedForPathSegment(token, pathExperience, dev2222Mode)
      if (cancelled) return
      markLoadPhase('shell-embed-resolved')
      await loadExperience(resolved)
    })()

    return () => {
      cancelled = true
      authRequestRef.current = null
    }
  }, [token, pathExperience, dev2222Mode])

  if (phase.kind === 'loading' || phase.kind === 'booting') {
    if (!showShellDevChrome(token)) return <ShellSplash />
    return (
      <>
        <ShellSplash />
        <DevShellBar
          fixed
          documentTheme={documentTheme}
          onDocumentThemeChange={persistDocumentTheme}
        />
      </>
    )
  }
  if (phase.kind === 'error') {
    return <div className="shell-unified-error">{phase.message}</div>
  }

  if (!showShellDevChrome(token)) return null

  return (
    <div className="shell-unified-pad">
      <p>
        <strong>{SHELL_APP_TITLE}</strong> → <strong>{phase.experience}</strong>
      </p>
      {phase.notice ? (
        <p className="shell-unified-notice">Shell auth unavailable; loaded {phase.experience}.</p>
      ) : null}
      <DevFeature2222Bar
        claimPreview={format2222ClaimForUi(phase.jwtPayload)}
        mode={dev2222Mode}
        onModeChange={persistDev2222Mode}
        documentTheme={documentTheme}
        onDocumentThemeChange={persistDocumentTheme}
      />
    </div>
  )
}
